import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  Box,
  IconButton,
  Divider,
  Grid2,
} from "@mui/material";
import FormSmallTextField from "../common/form/FormTextField";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import AppDropDown from "../common/filter/AppDropDown";
import { FilterValue } from "../../../models/Filters/FilterValue";
import DatePicker from "../common/textFields/DatePicker";
import AppSearchDropDown from "../common/filter/AppSearchDropDown";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import CloseIcon from "@mui/icons-material/Close";
import { ProjectRequest } from "../../../models/Project/Project";
import dayjs from "dayjs";
import { OrderType } from "../../../models/Project/OrderType";
import { useNotification } from "../../../utils/NotificationsContext";
import DatePicker2 from "../common/textFields/DatePicker2";
import {
  getOrderTypeFieldConfig,
  getPaymentFieldVisibilityFromLabel,
  ProjectRequestFieldKey,
  STOCK_CHECK_NOT_REQUIRED_FIELD_CONFIG,
} from "./projectRequestFieldConfig";
import { PROJECT_REQUEST_PAYMENT_TERM_OPTIONS } from "../../../models/Project/ProjectRequestPayment";

interface ProjectRequestPopupProps {
  open: boolean;
  currentFormData?: FormData;
  expedited_fees_added: boolean;
  stockCheckNotRequired?: boolean;
  onClose: () => void;
  onSubmit: (formData: Partial<ProjectRequest>) => void;
}

const isDeliveryOptions: FilterValue[] = [
  { id: "0", value: "No" },
  { id: "1", value: "Yes" },
];

const mockupStatus: FilterValue[] = [
  { id: "2", value: "Pending" },
  { id: "1", value: "Approved" },
];

/** Z-index for dropdowns/poppers so they appear above the dialog (dialog uses 2000) */
const POPUP_MENU_Z_INDEX = 2100;
const getDefaultDeliveryDate = () => dayjs().add(12, "day").toDate();
const getConditionalDefaultDeliveryDate = (orderTypeId?: string) => {
  const orderTypeConfig = getOrderTypeFieldConfig(orderTypeId);
  return orderTypeConfig.show.expected_delivery_date
    ? getDefaultDeliveryDate()
    : null;
};

export interface FormData {
  brand_name: string;
  client_poc_contact_no: string;
  client_poc: string;
  company_name: string;
  shipping_type_id: string;
  shipment_mode_id: string;
  payment_status_id: string;
  payment_term: string;
  delivery_date: Date | null;
  dispatch_date: Date | null;
  amount: string;
  purchase_order: string;
  is_delivery_date_flexible: string;
  is_diy_redemption: string;
  mockup_status_id: string;
  order_type_id: string;
  order_additional_info: string;
  warehouse_id: string | null;
}

interface FormDataErrors {
  [key: string]: string | undefined;
}

const FORM_TO_CONFIG_FIELD_KEY_MAP: Partial<
  Record<keyof FormData, ProjectRequestFieldKey>
> = {
  client_poc: "client_poc_name",
  client_poc_contact_no: "client_poc_number",
  shipment_mode_id: "mode_of_shipment",
  shipping_type_id: "type_of_shipping",
  payment_status_id: "payment_status",
  payment_term: "payment_term",
  delivery_date: "expected_delivery_date",
  is_delivery_date_flexible: "is_delivery_time_flexible",
  is_diy_redemption: "diy_redemption",
  mockup_status_id: "mockup_status",
  order_type_id: "order_type",
  order_additional_info: "order_notes",
  warehouse_id: "warehouse",
};

const ProjectRequestPopup: React.FC<ProjectRequestPopupProps> = ({
  open,
  currentFormData,
  expedited_fees_added,
  stockCheckNotRequired = false,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();

  const [formData, setFormData] = useState<FormData | undefined>(undefined);

  const { projectStore, warehouseStore } = useStores();
  const { showError } = useNotification();

  const [touched, setTouched] = useState<{ [key in keyof FormData]?: boolean }>(
    {},
  );
  const [errors, setErrors] = useState<FormDataErrors>({});
  const activeFieldConfig = stockCheckNotRequired
    ? STOCK_CHECK_NOT_REQUIRED_FIELD_CONFIG
    : getOrderTypeFieldConfig(formData?.order_type_id);
  const getConfigFieldKey = (field: keyof FormData): ProjectRequestFieldKey =>
    FORM_TO_CONFIG_FIELD_KEY_MAP[field] ?? (field as ProjectRequestFieldKey);

  const getPaymentStatusLabel = (paymentStatusId?: string) => {
    if (!paymentStatusId) return "";

    const status = projectStore.paymentStatuses?.find(
      (item) => Number(item.id) === Number(paymentStatusId),
    );

    return status?.value?.trim().toLowerCase() ?? "";
  };

  const getPaymentFieldVisibility = (paymentStatusId?: string) =>
    getPaymentFieldVisibilityFromLabel(getPaymentStatusLabel(paymentStatusId));

  const paymentFieldVisibility = getPaymentFieldVisibility(
    formData?.payment_status_id,
  );

  const hasPaymentStatusSelected = Boolean(
    formData?.payment_status_id?.toString().trim(),
  );

  const isPaidOrPartialPaymentStatus = (paymentStatusId?: string) =>
    getPaymentFieldVisibility(paymentStatusId).showAmount;

  const isPurchaseOrderPaymentStatus = (paymentStatusId?: string) =>
    getPaymentFieldVisibility(paymentStatusId).showPurchaseOrder;

  const isFieldVisible = (field: keyof FormData) => {
    const configField = getConfigFieldKey(field);
    if (!(configField in activeFieldConfig.show)) {
      return false;
    }

    if (!activeFieldConfig.show[configField]) {
      return false;
    }

    if (
      field === "payment_term" ||
      field === "amount" ||
      field === "purchase_order"
    ) {
      if (!hasPaymentStatusSelected) {
        return false;
      }
    }

    if (
      field === "payment_term" &&
      !paymentFieldVisibility.showPaymentTerm
    ) {
      return false;
    }

    if (
      field === "amount" &&
      !isPaidOrPartialPaymentStatus(formData?.payment_status_id)
    ) {
      return false;
    }

    if (
      field === "purchase_order" &&
      !isPurchaseOrderPaymentStatus(formData?.payment_status_id)
    ) {
      return false;
    }

    return true;
  };

  const isFieldRequired = (field: keyof FormData) => {
    if (
      field === "payment_term" ||
      field === "amount" ||
      field === "purchase_order"
    ) {
      return isFieldVisible(field);
    }

    const configField = getConfigFieldKey(field);
    if (!(configField in activeFieldConfig.required)) {
      return false;
    }

    return Boolean(activeFieldConfig.required[configField]);
  };
  const hasCompanyContactSection =
    isFieldVisible("company_name") ||
    isFieldVisible("brand_name") ||
    isFieldVisible("client_poc") ||
    isFieldVisible("client_poc_contact_no");
  const hasOrderTypeWarehouseSection =
    isFieldVisible("order_type_id") || isFieldVisible("warehouse_id");
  const hasDeliveryDatesSection =
    isFieldVisible("delivery_date") ||
    isFieldVisible("is_delivery_date_flexible") ||
    isFieldVisible("mockup_status_id") ||
    isFieldVisible("is_diy_redemption");
  const hasShipmentSection =
    isFieldVisible("shipment_mode_id") ||
    isFieldVisible("shipping_type_id") ||
    isFieldVisible("dispatch_date");
  const hasPaymentSection = isFieldVisible("payment_status_id");

  useEffect(() => {
    fetchWarehouses();

    if (currentFormData) {
      setFormData(currentFormData);
    } else {
      setFormData({
        brand_name: "Test",
        client_poc_contact_no: "",
        client_poc: "",
        company_name: "",
        delivery_date: getConditionalDefaultDeliveryDate(),
        dispatch_date: null,
        amount: "",
        purchase_order: "",
        is_delivery_date_flexible: "",
        shipping_type_id: "",
        shipment_mode_id: "",
        payment_status_id: "",
        payment_term: "",
        is_diy_redemption: "",
        mockup_status_id: "",
        order_type_id: "",
        order_additional_info: "",
        warehouse_id: null,
      });
    }
  }, [currentFormData]);

  const fetchWarehouses = async () => {
    try {
      if (warehouseStore.warehouses.length > 0) {
        return;
      }

      await warehouseStore.searchWarehouse();
    } catch (error) {
      console.error(error);
      showError("Error", "Failed to fetch warehouses.");
    }
  };

  const validate = (): boolean => {
    const newErrors: FormDataErrors = {};
    let isValid = true;

    if (!formData) return false;

    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof FormData;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    setErrors(newErrors);
    return isValid;
  };

  const validateField = (name: keyof FormData, value: any): string => {
    if (!isFieldVisible(name)) {
      return "";
    }

    if (
      isFieldRequired(name) &&
      (!value || (typeof value === "string" && value.trim() === ""))
    ) {
      return `${name.replace(/_/g, " ")} is required`;
    }
    if (name === "delivery_date") {
      if (!(value instanceof Date) || isNaN(value.getTime())) {
        return "Please select a valid delivery date";
      }
      if (dayjs(value).isBefore(dayjs(), "day")) {
        return "Delivery date cannot be in the past";
      }
      if (
        !expedited_fees_added &&
        dayjs(value).isBefore(dayjs().add(12, "day"), "day")
      ) {
        return "Delivery date must be 12 days or more from the current date";
      }
    }
    return "";
  };

  const handleChange = (id: keyof FormData, value: any) => {
    setFormData((prevData) => {
      if (!prevData) return prevData;
      const nextData: FormData = {
        ...prevData,
        [id]: value,
      };

      if (id === "order_type_id") {
        const shouldShowDeliveryDate = getOrderTypeFieldConfig(String(value))
          .show.expected_delivery_date;
        if (!shouldShowDeliveryDate) {
          nextData.delivery_date = null;
        } else if (!nextData.delivery_date) {
          nextData.delivery_date = getDefaultDeliveryDate();
        }
      }

      if (id === "payment_status_id") {
        const statusId = String(value ?? "").trim();
        if (!statusId) {
          nextData.payment_term = "";
          nextData.amount = "";
          nextData.purchase_order = "";
        } else {
          const visibility = getPaymentFieldVisibility(statusId);
          if (!visibility.showPaymentTerm) {
            nextData.payment_term = "";
          }
          if (!visibility.showAmount) {
            nextData.amount = "";
          }
          if (!visibility.showPurchaseOrder) {
            nextData.purchase_order = "";
          }
        }
      }

      return nextData;
    });

    setTouched((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [id]: true,
      };
    });

    const effectiveDeliveryDate =
      id === "delivery_date" ? value : formData?.delivery_date;
    const effectiveShipmentModeId =
      id === "shipment_mode_id" ? value : formData?.shipment_mode_id;
    if (effectiveDeliveryDate != null && effectiveShipmentModeId) {
      const dispatchDate = dayjs(effectiveDeliveryDate).subtract(4, "day");

      setFormData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          dispatch_date: dispatchDate.toDate(),
        };
      });

      setTouched((prev) => ({
        ...prev,
        dispatch_date: true,
      }));
    }

    const error = validateField(id, value);
    if (!error) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        if (
          id === "payment_status_id" &&
          !isPaidOrPartialPaymentStatus(String(value))
        ) {
          delete newErrors.amount;
        }
        if (
          id === "payment_status_id" &&
          !isPurchaseOrderPaymentStatus(String(value))
        ) {
          delete newErrors.purchase_order;
        }
        return newErrors;
      });
    } else if (touched[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: error,
      }));
    }
  };

  const handleDateChange = (newDate: Date | null) => {
    handleChange("delivery_date", newDate);
  };

  const handleSubmit = () => {
    if (!formData) return;

    if (validate()) {
      const payload: Partial<ProjectRequest> = {
        brand_name: formData?.brand_name,
        client_poc_contact_no: isFieldVisible("client_poc_contact_no")
          ? formData?.client_poc_contact_no
          : undefined,
        client_poc: isFieldVisible("client_poc")
          ? formData?.client_poc
          : undefined,
        company_name: formData?.company_name,
        shipping_type_id: isFieldVisible("shipping_type_id")
          ? Number(formData?.shipping_type_id)
          : undefined,
        shipment_mode_id: isFieldVisible("shipment_mode_id")
          ? Number(formData?.shipment_mode_id)
          : undefined,
        payment_status_id: isFieldVisible("payment_status_id")
          ? Number(formData?.payment_status_id)
          : undefined,
        payment_term: isFieldVisible("payment_term")
          ? formData?.payment_term || undefined
          : undefined,
        delivery_date: isFieldVisible("delivery_date")
          ? dayjs(formData?.delivery_date).format("YYYY-MM-DD")
          : undefined,
        dispatch_date: isFieldVisible("dispatch_date")
          ? dayjs(formData?.dispatch_date).format("YYYY-MM-DD")
          : undefined,
        amount: isFieldVisible("amount") ? formData?.amount : undefined,
        purchase_order: isFieldVisible("purchase_order")
          ? formData?.purchase_order
          : undefined,
        is_delivery_date_flexible: isFieldVisible("is_delivery_date_flexible")
          ? Number(formData?.is_delivery_date_flexible)
          : undefined,
        is_diy_redemption: isFieldVisible("is_diy_redemption")
          ? Number(formData?.is_diy_redemption)
          : undefined,
        mockup_status_id: isFieldVisible("mockup_status_id")
          ? Number(formData?.mockup_status_id)
          : undefined,
        order_additional_info: isFieldVisible("order_additional_info")
          ? formData?.order_additional_info
          : undefined,
        warehouse_id: isFieldVisible("warehouse_id")
          ? formData.warehouse_id
          : undefined,
      };

      onSubmit(payload);
    }
  };

  const renderError = (fieldName: keyof FormData) => {
    if (touched[fieldName] && errors[fieldName]) {
      return (
        <CustomTypography
          variant="body3"
          style={{
            color: theme.palette.error.main,
            marginTop: "2px",
          }}
        >
          {errors[fieldName]}
        </CustomTypography>
      );
    }
    return null;
  };

  const resetForm = () => {
    setFormData(
      currentFormData ?? {
        brand_name: "",
        client_poc_contact_no: "",
        client_poc: "",
        company_name: "",
        delivery_date: getConditionalDefaultDeliveryDate(),
        dispatch_date: null,
        amount: "",
        purchase_order: "",
        is_delivery_date_flexible: "",
        shipping_type_id: "",
        shipment_mode_id: "",
        payment_status_id: "",
        payment_term: "",
        is_diy_redemption: "",
        mockup_status_id: "",
        order_type_id: "",
        order_additional_info: "",
        warehouse_id: null,
      },
    );

    setErrors({});
    setTouched({});
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "24px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Create Project Request
          </CustomTypography>
          <IconButton
            aria-label="close"
            onClick={() => {
              resetForm();
              onClose();
            }}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {!stockCheckNotRequired && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: theme.palette.customColors?.lightGreyColor,
                border: `1px solid ${theme.palette.customColors?.greyBorder}`,
              }}
            >
              <CustomTypography
                variant="body3"
                sx={{
                  color: theme.palette.customColors?.greyTextColor,
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                <b>Please note:</b> The delivery date must be <b>12 days</b> or
                more from the current date. <br />
                For delivery dates falling within 12 days, the Sales Team shall
                ensure that <b>Expedited Fees</b> is added to the order.
              </CustomTypography>
            </Box>
          )}
          <Box>
            {hasCompanyContactSection && (
              <>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 20 }}
                >
                  Company & Contact
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                  {isFieldVisible("company_name") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        id="company_name"
                        label="Company Name"
                        fontSize="0.9rem"
                        value={formData?.company_name}
                        onChange={(id, value) =>
                          handleChange("company_name", value!)
                        }
                        required={isFieldRequired("company_name")}
                      />
                      {renderError("company_name")}
                    </Grid2>
                  )}
                  {isFieldVisible("brand_name") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        id="brand_name"
                        label="Brand Name"
                        fontSize="0.9rem"
                        value={formData?.brand_name}
                        onChange={(id, value) =>
                          handleChange("brand_name", value!)
                        }
                        required={isFieldRequired("brand_name")}
                      />
                      {renderError("brand_name")}
                    </Grid2>
                  )}
                  {isFieldVisible("client_poc") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        id="client_poc"
                        label="Client POC Name"
                        fontSize="0.9rem"
                        value={formData?.client_poc}
                        onChange={(id, value) =>
                          handleChange("client_poc", value!)
                        }
                        required={isFieldRequired("client_poc")}
                      />
                      {renderError("client_poc")}
                    </Grid2>
                  )}
                  {isFieldVisible("client_poc_contact_no") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        type="text"
                        id="client_poc_contact_no"
                        label="Client POC Number"
                        fontSize="0.9rem"
                        value={formData?.client_poc_contact_no || ""}
                        onChange={(id, value) =>
                          handleChange("client_poc_contact_no", value!)
                        }
                        required={isFieldRequired("client_poc_contact_no")}
                      />
                      {renderError("client_poc_contact_no")}
                    </Grid2>
                  )}
                </Grid2>
              </>
            )}

            {hasOrderTypeWarehouseSection && (
              <>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginTop: 20, marginBottom: 20 }}
                >
                  Order Type & Warehouse
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                  {isFieldVisible("order_type_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        value={formData?.order_type_id}
                        list={OrderType}
                        fontSize="0.9rem"
                        isMultiSelect={false}
                        title="Order Type"
                        onSingleSelectionChange={(val) => {
                          handleChange("order_type_id", val);
                        }}
                        required={isFieldRequired("order_type_id")}
                        menuZIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("order_type_id")}
                    </Grid2>
                  )}
                  {isFieldVisible("warehouse_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        value={formData?.warehouse_id}
                        list={warehouseStore.warehouses.map((item) => ({
                          id: item.id,
                          value: item.identifier,
                        }))}
                        fontSize="0.9rem"
                        isMultiSelect={false}
                        title="Warehouse"
                        onSingleSelectionChange={(val) => {
                          handleChange("warehouse_id", val);
                        }}
                        required={isFieldRequired("warehouse_id")}
                        menuZIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("warehouse_id")}
                    </Grid2>
                  )}
                </Grid2>
              </>
            )}

            {hasDeliveryDatesSection && (
              <>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginTop: 20, marginBottom: 20 }}
                >
                  Delivery & Dates
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                  {isFieldVisible("delivery_date") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <DatePicker2
                        label="Expected Delivery Date*"
                        fontSize="0.9rem"
                        type="date"
                        minDate={
                          expedited_fees_added
                            ? dayjs()
                            : dayjs().add(12, "day")
                        }
                        value={
                          formData?.delivery_date
                            ? dayjs(formData?.delivery_date)
                            : null
                        }
                        onChange={(newValue) => {
                          handleChange(
                            "delivery_date",
                            newValue ? newValue.toDate() : null,
                          );
                        }}
                        require={isFieldRequired("delivery_date")}
                        zIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("delivery_date")}
                    </Grid2>
                  )}

                  {isFieldVisible("is_delivery_date_flexible") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        value={formData?.is_delivery_date_flexible}
                        list={isDeliveryOptions}
                        fontSize="0.9rem"
                        isMultiSelect={false}
                        title="Is the delivery time flexible?"
                        onSingleSelectionChange={(val) => {
                          handleChange("is_delivery_date_flexible", val);
                        }}
                        required={isFieldRequired("is_delivery_date_flexible")}
                        menuZIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("is_delivery_date_flexible")}
                    </Grid2>
                  )}

                  {isFieldVisible("mockup_status_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        value={formData?.mockup_status_id}
                        list={mockupStatus}
                        fontSize="0.9rem"
                        isMultiSelect={false}
                        title="Mockup Status"
                        onSingleSelectionChange={(val) => {
                          handleChange("mockup_status_id", val);
                        }}
                        required={isFieldRequired("mockup_status_id")}
                        menuZIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("mockup_status_id")}
                    </Grid2>
                  )}

                  {isFieldVisible("is_diy_redemption") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        value={formData?.is_diy_redemption}
                        list={isDeliveryOptions}
                        fontSize="0.9rem"
                        isMultiSelect={false}
                        title="DIY Redemption"
                        onSingleSelectionChange={(val) => {
                          handleChange("is_diy_redemption", val);
                        }}
                        required={isFieldRequired("is_diy_redemption")}
                        menuZIndex={POPUP_MENU_Z_INDEX}
                      />
                      {renderError("is_diy_redemption")}
                    </Grid2>
                  )}
                </Grid2>
              </>
            )}

            {hasShipmentSection && (
              <>
                <Divider sx={{ my: 3 }} />
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginTop: 20, marginBottom: 10 }}
                >
                  Shipment
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                  {isFieldVisible("shipment_mode_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppSearchDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        isMultiSelect={false}
                        required={isFieldRequired("shipment_mode_id")}
                        title="Mode Of Shipment"
                        fontSize="0.9rem"
                        local={true}
                        key={`shipment_mode_id`}
                        value={
                          formData?.shipment_mode_id
                            ? [
                                {
                                  id: String(formData?.shipment_mode_id),
                                  value: projectStore.shipmentModes?.find(
                                    (order) =>
                                      Number(order.id) ===
                                      Number(formData?.shipment_mode_id),
                                  )?.value!,
                                },
                              ]
                            : []
                        }
                        list={
                          projectStore.shipmentModes.map((item) => ({
                            id: String(item.id),
                            value: item.value!,
                          })) || []
                        }
                        handleSelect={(value: FilterValue[]) => {
                          if (value.length > 0) {
                            handleChange(
                              "shipment_mode_id",
                              value[0].id ? value[0].id : "",
                            );
                          } else {
                            handleChange("shipment_mode_id", "");
                          }
                        }}
                      />
                      {renderError("shipment_mode_id")}
                    </Grid2>
                  )}

                  {isFieldVisible("shipping_type_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppSearchDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        isMultiSelect={false}
                        required={isFieldRequired("shipping_type_id")}
                        title="Type Of Shipping"
                        fontSize="0.9rem"
                        local={true}
                        key={`shipping_type_id`}
                        value={
                          formData?.shipping_type_id
                            ? [
                                {
                                  id: String(formData?.shipping_type_id),
                                  value: projectStore.shipmentType?.find(
                                    (order) =>
                                      Number(order.id) ===
                                      Number(formData?.shipping_type_id),
                                  )?.value!,
                                },
                              ]
                            : []
                        }
                        list={
                          projectStore.shipmentType.map((item) => ({
                            id: String(item.id),
                            value: item.value!,
                          })) || []
                        }
                        handleSelect={(value: FilterValue[]) => {
                          if (value.length > 0) {
                            handleChange(
                              "shipping_type_id",
                              value[0].id ? value[0].id : "",
                            );
                          } else {
                            handleChange("shipping_type_id", "");
                          }
                        }}
                      />
                      {renderError("shipping_type_id")}
                    </Grid2>
                  )}

                  {isFieldVisible("dispatch_date") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <DatePicker
                        label="Dispatch Date*"
                        disabled={true}
                        fontSize="0.9rem"
                        value={formData?.dispatch_date || null}
                        onChange={() => {}}
                        require={isFieldRequired("dispatch_date")}
                      />
                      {renderError("dispatch_date")}
                    </Grid2>
                  )}
                </Grid2>
              </>
            )}

            {hasPaymentSection && (
              <>
                <Divider sx={{ my: 3 }} />
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginTop: 20, marginBottom: 10 }}
                >
                  Payment
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                  {isFieldVisible("payment_status_id") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppSearchDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        isMultiSelect={false}
                        required={isFieldRequired("payment_status_id")}
                        title="Payment Status"
                        fontSize="0.9rem"
                        local={true}
                        key={`payment_status_id`}
                        value={
                          formData?.payment_status_id
                            ? [
                                {
                                  id: String(formData?.payment_status_id),
                                  value: projectStore.paymentStatuses?.find(
                                    (order) =>
                                      Number(order.id) ===
                                      Number(formData?.payment_status_id),
                                  )?.value!,
                                },
                              ]
                            : []
                        }
                        list={
                          projectStore.paymentStatuses.map((item) => ({
                            id: String(item.id),
                            value: item.value!,
                          })) || []
                        }
                        handleSelect={(value: FilterValue[]) => {
                          if (value.length > 0) {
                            handleChange(
                              "payment_status_id",
                              value[0].id ? value[0].id : "",
                            );
                          } else {
                            handleChange("payment_status_id", "");
                          }
                        }}
                      />
                      {renderError("payment_status_id")}
                    </Grid2>
                  )}

                  {isFieldVisible("payment_term") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <AppSearchDropDown
                        borderColor={theme.palette.customColors.blackBorder}
                        isMultiSelect={false}
                        required={isFieldRequired("payment_term")}
                        title="Payment Term"
                        fontSize="0.9rem"
                        local={true}
                        key="payment_term"
                        value={
                          formData?.payment_term
                            ? [
                                {
                                  id: formData.payment_term,
                                  value: formData.payment_term,
                                },
                              ]
                            : []
                        }
                        list={PROJECT_REQUEST_PAYMENT_TERM_OPTIONS.map(
                          (option) => ({
                            id: option.id,
                            value: option.value,
                          }),
                        )}
                        handleSelect={(value: FilterValue[]) => {
                          if (value.length > 0) {
                            handleChange(
                              "payment_term",
                              value[0].id ? value[0].id : "",
                            );
                          } else {
                            handleChange("payment_term", "");
                          }
                        }}
                      />
                      {renderError("payment_term")}
                    </Grid2>
                  )}

                  {isFieldVisible("amount") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        id="amount"
                        label="Amount"
                        fontSize="0.9rem"
                        value={formData?.amount || ""}
                        onChange={(id, value) =>
                          handleChange(
                            "amount",
                            (value ?? "").replace(/\D/g, ""),
                          )
                        }
                        required={isFieldRequired("amount")}
                      />
                      {renderError("amount")}
                    </Grid2>
                  )}

                  {isFieldVisible("purchase_order") && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormSmallTextField
                        sx={{ mb: 0 }}
                        id="purchase_order"
                        label="Purchase Order Id"
                        fontSize="0.9rem"
                        value={formData?.purchase_order || ""}
                        onChange={(id, value) =>
                          handleChange(
                            "purchase_order",
                            (value ?? "").replace(/\D/g, ""),
                          )
                        }
                        required={isFieldRequired("purchase_order")}
                      />
                      {renderError("purchase_order")}
                    </Grid2>
                  )}
                </Grid2>
              </>
            )}
          </Box>

          {isFieldVisible("order_additional_info") && (
            <>
              <Divider sx={{ my: 3 }} />
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Order Notes
              </CustomTypography>
              <Box sx={{ flex: "1 1 100%" }}>
                <FormSmallTextField
                  id="order_additional_info"
                  placeholder="You can add any additional information here. Eg. Product wise initial information, packing instructions, link to the drive with logos and creatives, etc."
                  label="Order Notes"
                  sx={{ mb: 0 }}
                  fontSize="0.9rem"
                  value={formData?.order_additional_info}
                  onChange={(id, value) =>
                    handleChange("order_additional_info", value!)
                  }
                  multiline
                  rows={3}
                  required={isFieldRequired("order_additional_info")}
                />
                {renderError("order_additional_info")}
                <CustomTypography
                  variant="body3"
                  color={theme.palette.customColors.greyTextColor}
                >
                  This is a required field. Put NA in case you don't have it
                  yet.
                </CustomTypography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box />
          <Box sx={{ display: "flex", gap: 2 }}>
            <AppButton
              variant="grayscale"
              onClick={() => {
                resetForm();
                onClose();
              }}
              label="Cancel"
            />
            <AppButton
              variant="primary"
              onClick={handleSubmit}
              label="Submit"
            />
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(ProjectRequestPopup);
